import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Loader2, AlertCircle, ArrowRight, Sparkles } from 'lucide-react';
import { Input } from './ui/input';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import { validateInvite } from '@/lib/supabase/invites';

interface InviteCodeSignupProps {
  className?: string;
}

const InviteCodeSignup: React.FC<InviteCodeSignupProps> = ({ className }) => {
  const [inviteCode, setInviteCode] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const shouldPulseButton = useInviteCodeAnimation(inviteCode);

  // Check for invite code in URL or localStorage on mount
  useEffect(() => {
    // Check URL parameters first
    const params = new URLSearchParams(window.location.search);
    const codeFromUrl = params.get('code');
    
    if (codeFromUrl) {
      setInviteCode(codeFromUrl);
      return;
    }
    
    // Check localStorage as fallback (for Android referrer API integration)
    try {
      const storedCode = localStorage.getItem('playclub_invite_code');
      if (storedCode) {
        setInviteCode(storedCode);
      }
    } catch (err) {
      console.warn('Could not access localStorage:', err);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!inviteCode.trim()) {
      setError('Please enter an invite code');
      return;
    }
    
    setIsValidating(true);
    setError(null);
    
    try {
      // Validate the invite code
      const inviteData = await validateInvite(inviteCode);
      
      if (!inviteData) {
        setError('Failed to validate invite code. Please try again.');
        return;
      }
      
      if (!inviteData.valid) {
        // Use the specific error message from the backend if available
        setError(inviteData.message || 'Invalid or expired invite code');
        return;
      }
      
      // Store the code in localStorage for the onboarding flow
      localStorage.setItem('playclub_invite_code', inviteCode);
      
      // Redirect to onboarding with the code
      navigate(`/onboarding?code=${inviteCode}`);
    } catch (err) {
      console.error('Error validating invite code:', err);
      // Display the specific error message from the backend if available
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Failed to validate invite code. Please try again.');
      }
    } finally {
      setIsValidating(false);
    }
  };

  return (
    <motion.div 
      className={cn(
        "w-full max-w-md mx-auto px-4 py-5 mt-2",
        "bg-gradient-to-b from-dark-100/95 to-dark-200/95 backdrop-blur-sm rounded-xl border-2 border-gold-400/40",
        "shadow-lg shadow-gold-400/10",
        "animate-pulse-slow relative",
        className
      )}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3, duration: 0.5 }}
    >
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-white mb-2 !text-white relative inline-flex items-center gap-2">
          <Sparkles className="h-6 w-6 text-gold-400 animate-pulse" />
          Have an Invite Code?
          <Sparkles className="h-6 w-6 text-gold-400 animate-pulse" />
        </h2>
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <div className="relative">
            <Input
              value={inviteCode}
              onChange={(e) => {
                setInviteCode(e.target.value);
                if (error) setError(null);
              }} 
              placeholder="Enter invite code"
              aria-label="Invite code input"
              className={cn(
                "bg-dark-300/90 border-gold-400/40 text-white !text-white h-14 pr-12 text-center uppercase tracking-wider font-bold text-xl",
                "focus:border-gold-400 focus:ring-2 focus:ring-gold-400/50 placeholder:text-white/50",
                "border-2 shadow-[0_0_25px_rgba(251,191,36,0.5)] transition-all duration-300",
                error && "border-red-500 focus:border-red-500 focus:ring-red-500/50"
              )}
            />
            <button
              type="submit"
              disabled={isValidating || !inviteCode.trim()}
              className={cn(
                "absolute right-0 top-0 bottom-0 px-3 flex items-center justify-center group overflow-hidden",
                "transition-all duration-300",
                inviteCode.trim()
                  ? "text-gold-400 hover:text-white hover:bg-gold-400 rounded-r-md shadow-lg"
                  : "text-dark-800 cursor-not-allowed",
                (shouldPulseButton && inviteCode.trim()) && "pulse-button"
              )}
            >
              {isValidating ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <>
                  {/* Background glow effect */}
                  <div className={cn(
                    "absolute inset-0 opacity-0 bg-gold-400/20",
                    inviteCode.trim() && "group-hover:opacity-100 transition-opacity duration-300"
                  )} />
                  
                  {/* Arrow icon */}
                  <ArrowRight className={cn(
                    "h-6 w-6 transition-all duration-300 relative z-10",
                    inviteCode.trim() && "group-hover:translate-x-1 group-hover:scale-110"
                  )} />
                </>
              )}
            </button>
          </div>
          {error && (
            <div className="flex items-center mt-2 text-red-500 text-sm font-medium">
              <AlertCircle className="h-4 w-4 mr-1 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
          <div className="text-center mt-4 text-white/60 text-sm">
          </div>
          <div className="text-center mt-4 text-white/60 text-sm">
            <span>Already a member? <button onClick={() => document.querySelector<HTMLButtonElement>('button:has(.relative.z-10.font-medium)')?.click()} className="text-gold-400 hover:text-gold-300 hover:underline font-medium transition-colors">Sign in here</button></span>
          </div>
        </div>
      </form>
    </motion.div>
  );
};

// Add a pulse animation when code is entered
const useInviteCodeAnimation = (inviteCode: string) => {
  const [shouldAnimate, setShouldAnimate] = useState(false);
  
  useEffect(() => {
    if (inviteCode.trim()) {
      setShouldAnimate(true);
      const timer = setTimeout(() => {
        setShouldAnimate(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [inviteCode]);
  
  return shouldAnimate;
};

export default InviteCodeSignup;